import { useEffect, useState, useCallback, useRef } from "react";
import { IResourceData } from "../types/resource";
import { getResourceList } from "../services/resourcesService";

interface IuseFetchResources {
  currentDomainId?: string | number | null;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchOnMount?: boolean; // Controls whether to fetch data immediately on mount
}

const useFetchResources = ({
  currentDomainId,
  setIsLoading,
  fetchOnMount = true, // Default to true for backward compatibility
}: IuseFetchResources) => {
  const [resourcesData, setResourcesData] = useState<IResourceData[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false); // Internal loading state for Autocomplete

  // Use refs for guards and mutable props so the callback identity stays stable,
  // preventing useEffect re-triggers from unrelated re-renders.
  const hasFetchedRef = useRef<boolean>(false);
  const isFetchingRef = useRef<boolean>(false);
  const setIsLoadingRef = useRef(setIsLoading);
  setIsLoadingRef.current = setIsLoading;

  const currentDomainIdRef = useRef(currentDomainId);
  currentDomainIdRef.current = currentDomainId;

  const fetchResources = useCallback(async () => {
    const domainId = currentDomainIdRef.current;

    if (!domainId || Number(domainId) <= 0) {
      setResourcesData([]);
      return;
    }

    // Prevent duplicate fetches AND concurrent in-flight requests
    if (hasFetchedRef.current || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setIsFetching(true);
    setIsLoadingRef.current(true);
    try {
      const result: IResourceData[] = await getResourceList({
        currentDomainId: domainId,
      });
      const reversedResult = result && result.reverse();
      setResourcesData(reversedResult || []);
      hasFetchedRef.current = true;
    } catch (err) {
      console.log("useFetchResources error:", err);
      setResourcesData([]); // Always return empty array instead of undefined
    } finally {
      isFetchingRef.current = false;
      setIsFetching(false);
      setIsLoadingRef.current(false);
    }
  }, []); // No deps — uses refs for all mutable values

  // Reset hasFetched when currentDomainId changes so data is re-fetched for the new domain
  useEffect(() => {
    hasFetchedRef.current = false;
    if (fetchOnMount && currentDomainId && Number(currentDomainId) > 0) {
      fetchResources();
    }
  }, [fetchOnMount, fetchResources, currentDomainId]);

  return [resourcesData, fetchResources, isFetching] as const;
};

export default useFetchResources;
